import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate, fmtINR } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

export default function HisaabPrint() {
  const [sp] = useSearchParams();
  const customer_id = sp.get("customer_id") || "";
  const project_id = sp.get("project_id") || "";
  const category = sp.get("category") || "";
  const from_date = sp.get("from_date") || "";
  const to_date = sp.get("to_date") || "";

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [company, setCompany] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [project, setProject] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams();
        if (customer_id) params.set("customer_id", customer_id);
        if (project_id) params.set("project_id", project_id);
        if (category) params.set("category", category);
        if (from_date) params.set("from_date", from_date);
        if (to_date) params.set("to_date", to_date);
        const qs = params.toString();

        const [hisaab, sum, comp] = await Promise.all([
          api.get(`/hisaab${qs?`?${qs}`:""}`).then(r=>r.data).catch(()=>[]),
          api.get(`/hisaab/summary${qs?`?${qs}`:""}`).then(r=>r.data).catch(()=>null),
          api.get("/settings/company").then(r=>r.data).catch(()=>null),
        ]);
        setRows(hisaab); setSummary(sum); setCompany(comp);

        if (customer_id) {
          const [c, pays, projs] = await Promise.all([
            api.get(`/customers/${customer_id}`).then(r=>r.data).catch(()=>null),
            api.get(`/payments?customer_id=${customer_id}`).then(r=>r.data).catch(()=>[]),
            api.get("/projects").then(r=>r.data).catch(()=>[]),
          ]);
          setCustomer(c);
          setPayments(pays);
          const custProjs = projs.filter(p => p.customer_id === customer_id);
          if (project_id) setProject(custProjs.find(p => p.id === project_id) || custProjs[0] || null);
          else setProject(custProjs[0] || null);
        }
      } finally { setLoading(false); }
    })();
  }, [customer_id, project_id, category, from_date, to_date]);

  if (loading) return <div className="p-10 text-center text-slate-500">Loading hisaab report...</div>;

  const canon = (c) => c === "income" ? "project_amount" : (c === "not_used" ? "other_credit" : c);

  const receivedTotal = payments.filter(p => p.direction === "IN").reduce((s, p) => s + (parseFloat(p.amount)||0), 0);
  const paymentModes = [...new Set(payments.filter(p=>p.direction==="IN").map(p => p.mode).filter(Boolean))].join(" / ") || "—";

  // Categorized rows
  const projectRows = rows.filter(r => canon(r.category) === "project_amount");
  const usedRows = rows.filter(r => canon(r.category) === "used");
  const expenseRows = rows.filter(r => canon(r.category) === "expense");
  const otherCrRows = rows.filter(r => canon(r.category) === "other_credit");
  const otherDbRows = rows.filter(r => canon(r.category) === "other_debit");

  const sum = (arr) => arr.reduce((s, r) => s + (parseFloat(r.amount)||0), 0);
  const projectAmount = sum(projectRows);
  const usedAmount = sum(usedRows);
  const expenseAmount = sum(expenseRows);
  const otherCredit = sum(otherCrRows);
  const otherDebit = sum(otherDbRows);

  const totalCredit = projectAmount + otherCredit;
  const totalDebit = usedAmount + expenseAmount + otherDebit;
  const totalCost = usedAmount + expenseAmount;  // core "cost of doing the job"
  const profit = totalCredit - totalDebit;
  const balancePending = totalCredit - receivedTotal;

  // Cost line items = Used + Expense combined
  const costItems = [...usedRows, ...expenseRows];

  const Label = ({children}) => <div className="text-[9px] uppercase font-bold text-slate-100 bg-slate-700 px-1.5 py-0.5 border border-slate-800">{children}</div>;
  const Val = ({children, mono=false}) => <div className={`text-[11px] px-2 py-1 border border-slate-300 min-h-[24px] bg-white text-slate-900 ${mono?"font-mono":""} font-semibold`}>{children || <span className="text-slate-400">—</span>}</div>;

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      {/* Header bar — hidden on print */}
      <div className="no-print flex items-center justify-between bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10">
        <button onClick={() => window.close()} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="hisaab-print-back">
          <ArrowLeft className="w-4 h-4"/>Close
        </button>
        <div className="text-xs text-slate-500">Project Management Sheet — Ctrl+P to Save as PDF</div>
        <button onClick={() => window.print()} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="hisaab-print-btn">
          <Printer className="w-4 h-4"/>Print / Save PDF
        </button>
      </div>

      <div className="max-w-5xl mx-auto bg-white shadow-lg print:shadow-none p-6 my-4 print:my-0 print:p-3" data-testid="hisaab-print-page">
        {/* Top title bar */}
        <div className="bg-emerald-800 text-white text-center py-2 border-2 border-slate-900 mb-0">
          <div className="text-lg font-black tracking-widest uppercase" style={{fontFamily:'Outfit'}}>{company?.company_name || "MRM RENEWABLES"} — PROJECT MANAGEMENT</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-100">SOLAR PROJECT MANAGEMENT · HISAAB</div>
        </div>

        {/* Info grid — 4 rows × 4 cols */}
        <div className="grid grid-cols-8 border-l border-t border-slate-800 mb-0">
          <Label>Customer ID</Label><Val mono>{customer?.customer_no}</Val>
          <Label>Customer Name</Label><Val>{customer?.name}</Val>
          <Label>Site Address</Label><Val>{project?.site_address || customer?.address}</Val>
          <Label>Contact Person</Label><Val>{customer?.name}</Val>

          <Label>Mobile</Label><Val mono>{customer?.mobile}</Val>
          <Label>Capacity (kW)</Label><Val mono>{project?.system_capacity || customer?.solar_capacity}</Val>
          <Label>System Type</Label><Val>ONGRID</Val>
          <Label>Survey Date</Label><Val>{project?.created_at ? fmtDate(project.created_at) : "—"}</Val>

          <Label>Order Date</Label><Val>{project?.created_at ? fmtDate(project.created_at) : "—"}</Val>
          <Label>Install Start</Label><Val>{project?.installation_date ? fmtDate(project.installation_date) : "—"}</Val>
          <Label>Install Complete</Label><Val>{project?.commissioning_date ? fmtDate(project.commissioning_date) : (project?.status === "Completed" ? "COMPLETED" : "—")}</Val>
          <Label>Project Status</Label><Val>{project?.status || "—"}</Val>

          <Label>Payment Mode</Label><Val>{paymentModes}</Val>
          <Label>Received</Label><Val mono>{fmtINR(receivedTotal)}</Val>
          <Label>Project Amount</Label><Val mono>{fmtINR(projectAmount || totalCredit)}</Val>
          <Label>Remarks</Label><Val>{balancePending > 0 ? `${fmtINR(balancePending)} pending` : (balancePending < 0 ? "Advance received" : "Fully paid")}</Val>
        </div>

        {/* Line items — cost breakdown (Used + Expense, NO GST columns) */}
        <table className="w-full text-[10px] border-collapse mt-3">
          <thead>
            <tr className="bg-slate-700 text-white uppercase tracking-wider text-[9px]">
              <th className="p-1.5 border border-slate-900 w-8">S.No</th>
              <th className="p-1.5 border border-slate-900 text-left">Item Description</th>
              <th className="p-1.5 border border-slate-900 w-14">Unit</th>
              <th className="p-1.5 border border-slate-900 w-14 text-right">Qty</th>
              <th className="p-1.5 border border-slate-900 w-24 text-right">Rate</th>
              <th className="p-1.5 border border-slate-900 w-28 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {costItems.map((r, i) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`cost-row-${i}`}>
                <td className="p-1 border border-slate-300 text-center font-mono">{i+1}</td>
                <td className="p-1 border border-slate-300 font-medium">
                  {r.item_name}
                  <span className={`ml-1 text-[8px] uppercase font-bold ${canon(r.category)==="expense"?"text-amber-700":"text-rose-700"}`}>({canon(r.category)==="expense"?"exp":"used"})</span>
                </td>
                <td className="p-1 border border-slate-300 text-center">{r.unit || "Nos"}</td>
                <td className="p-1 border border-slate-300 text-right font-mono">{r.qty}</td>
                <td className="p-1 border border-slate-300 text-right font-mono">{fmtINR(r.rate)}</td>
                <td className="p-1 border border-slate-300 text-right font-mono font-semibold">{fmtINR(r.amount)}</td>
              </tr>
            ))}
            {costItems.length === 0 && (
              <tr><td colSpan="6" className="p-4 text-center text-slate-400 border border-slate-300">No cost items entered yet. Hisaab me "Kya Laga" ya "Expense" entries add karo.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 font-bold">
              <td colSpan="5" className="p-1.5 border border-slate-900 text-right uppercase text-[10px] tracking-wider">GRAND TOTAL (COST)</td>
              <td className="p-1.5 border border-slate-900 text-right font-mono" data-testid="grand-total-cost">{fmtINR(totalCost)}</td>
            </tr>
          </tfoot>
        </table>

        {/* 4-cell Summary Box */}
        <div className="mt-4 grid grid-cols-4 border-l border-t border-slate-900">
          <div className="border-r border-b border-slate-900">
            <div className="bg-emerald-700 text-white text-[9px] uppercase tracking-wider font-bold p-1.5 text-center">Project Amount</div>
            <div className="text-lg font-black font-mono text-center py-2 text-emerald-800" data-testid="summary-project-amount">{fmtINR(projectAmount || totalCredit)}</div>
          </div>
          <div className="border-r border-b border-slate-900">
            <div className="bg-rose-700 text-white text-[9px] uppercase tracking-wider font-bold p-1.5 text-center">Total Cost (Used+Exp)</div>
            <div className="text-lg font-black font-mono text-center py-2 text-rose-800" data-testid="summary-total-cost">{fmtINR(totalCost)}</div>
          </div>
          <div className="border-r border-b border-slate-900">
            <div className={`${profit>=0?"bg-emerald-800":"bg-rose-800"} text-white text-[9px] uppercase tracking-wider font-bold p-1.5 text-center`}>PROFIT</div>
            <div className={`text-lg font-black font-mono text-center py-2 ${profit>=0?"text-emerald-800":"text-rose-800"}`} data-testid="summary-profit">{fmtINR(profit)}</div>
          </div>
          <div className="border-r border-b border-slate-900">
            <div className="bg-slate-700 text-white text-[9px] uppercase tracking-wider font-bold p-1.5 text-center">Total Entries</div>
            <div className="text-lg font-black font-mono text-center py-2 text-slate-800">{rows.length}</div>
          </div>
        </div>

        {/* Other Credit / Debit / Received breakdown */}
        <div className="mt-2 grid grid-cols-4 border-l border-t border-slate-400">
          <div className="border-r border-b border-slate-400">
            <div className="bg-blue-50 text-[9px] uppercase tracking-wider font-bold p-1 text-center text-blue-800">Other Credit</div>
            <div className="text-sm font-bold font-mono text-center py-1 text-blue-700" data-testid="summary-other-credit">{fmtINR(otherCredit)}</div>
          </div>
          <div className="border-r border-b border-slate-400">
            <div className="bg-amber-50 text-[9px] uppercase tracking-wider font-bold p-1 text-center text-amber-800">Other Debit</div>
            <div className="text-sm font-bold font-mono text-center py-1 text-amber-700" data-testid="summary-other-debit">{fmtINR(otherDebit)}</div>
          </div>
          <div className="border-r border-b border-slate-400">
            <div className="bg-emerald-50 text-[9px] uppercase tracking-wider font-bold p-1 text-center text-emerald-800">Received</div>
            <div className="text-sm font-bold font-mono text-center py-1 text-emerald-700">{fmtINR(receivedTotal)}</div>
          </div>
          <div className="border-r border-b border-slate-400">
            <div className="bg-slate-100 text-[9px] uppercase tracking-wider font-bold p-1 text-center text-slate-700">Balance Pending</div>
            <div className={`text-sm font-bold font-mono text-center py-1 ${balancePending>0?"text-rose-700":"text-emerald-700"}`}>{fmtINR(balancePending)}</div>
          </div>
        </div>

        {/* Other Credit / Debit itemized (if any) */}
        {(otherCrRows.length > 0 || otherDbRows.length > 0) && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            {otherCrRows.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-blue-800 mb-1">Other Credit Entries ({otherCrRows.length})</div>
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="bg-blue-100 uppercase tracking-wider text-[9px] text-blue-900">
                      <th className="p-1 border border-slate-300 w-6">#</th>
                      <th className="p-1 border border-slate-300 text-left">Description</th>
                      <th className="p-1 border border-slate-300 w-20 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherCrRows.map((r,i)=>(
                      <tr key={r.id}>
                        <td className="p-1 border border-slate-300 text-center font-mono">{i+1}</td>
                        <td className="p-1 border border-slate-300 font-medium">{r.item_name}<div className="text-[9px] text-slate-500">{fmtDate(r.date)}</div></td>
                        <td className="p-1 border border-slate-300 text-right font-mono font-semibold text-blue-800">{fmtINR(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {otherDbRows.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-1">Other Debit Entries ({otherDbRows.length})</div>
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="bg-amber-100 uppercase tracking-wider text-[9px] text-amber-900">
                      <th className="p-1 border border-slate-300 w-6">#</th>
                      <th className="p-1 border border-slate-300 text-left">Description</th>
                      <th className="p-1 border border-slate-300 w-20 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherDbRows.map((r,i)=>(
                      <tr key={r.id}>
                        <td className="p-1 border border-slate-300 text-center font-mono">{i+1}</td>
                        <td className="p-1 border border-slate-300 font-medium">{r.item_name}<div className="text-[9px] text-slate-500">{fmtDate(r.date)}</div></td>
                        <td className="p-1 border border-slate-300 text-right font-mono font-semibold text-amber-800">{fmtINR(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Project Amount entries (if any) */}
        {projectRows.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 mb-1">Project Amount Entries ({projectRows.length})</div>
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-emerald-100 uppercase tracking-wider text-[9px] text-emerald-900">
                  <th className="p-1.5 border border-slate-300 w-8">S.No</th>
                  <th className="p-1.5 border border-slate-300 w-20">Date</th>
                  <th className="p-1.5 border border-slate-300 text-left">Description</th>
                  <th className="p-1.5 border border-slate-300 w-24 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {projectRows.map((r, i)=>(
                  <tr key={r.id}>
                    <td className="p-1 border border-slate-300 text-center font-mono">{i+1}</td>
                    <td className="p-1 border border-slate-300 text-center text-[10px]">{fmtDate(r.date)}</td>
                    <td className="p-1 border border-slate-300 font-medium">{r.item_name}</td>
                    <td className="p-1 border border-slate-300 text-right font-mono font-semibold">{fmtINR(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 pt-2 border-t border-slate-200 text-[8px] text-slate-500 flex justify-between">
          <div>Report by {company?.company_name || "MRM CRM"} · {company?.gstin?`GSTIN: ${company.gstin}`:""} · Generated: {fmtDate(new Date().toISOString())}</div>
          <div className="font-mono">For internal use only</div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
